/**
 * Every session's media, fetched once for the whole screen.
 *
 * Previously each `SessionRow` fetched its own media *and* its own head
 * coverage - two requests per row, and the coverage one only to fill a column
 * that is moving to the project-level summary. Removing that column removes
 * half the requests outright; hoisting the other half means the aggregate at
 * the bottom counts the same bytes the rows do, rather than issuing a second
 * round of identical calls to say the same thing.
 *
 * It is still one request per session, because the API has no project-level
 * media listing. That is a real N, not an accidental N+1: nothing here fetches
 * per *file*, and the rows consume what this already loaded.
 */

import { useEffect, useRef, useState } from "react";

import { getSessionMediaSummary, listSessionMedia } from "@/api/client";
import type { CaptureSession, MediaAsset, MediaCountSummary } from "@/api/types";

export interface CaptureTotals {
  photos: number;
  videos: number;
  media: number;
}

export interface CaptureSummary {
  /**
   * Assets by capture session id, as far as one page goes. Absent while that
   * session is still loading.
   *
   * **A page, not the session.** This is what the thumbnail strip draws, and it
   * is deliberately not what any number is counted from - see `countsBySession`.
   */
  bySession: Record<string, MediaAsset[]>;
  /**
   * The server's own totals per session, which is what every count on this
   * screen must come from.
   *
   * The bug this exists for was visible in a real browser: a session holding
   * 150 files said "100 medya", "+94", "FOTOĞRAF 100", "MEDYA 100" and
   * "Çekim Özeti: 100 Medya", while the project card beside it correctly said
   * "150 dosya". Every one of those numbers was `assets.length` on a response
   * the server had capped at a hundred. `/capture-sessions/{id}/media-summary`
   * exists precisely to answer this and was not being asked.
   */
  countsBySession: Record<string, MediaCountSummary>;
  totals: CaptureTotals;
  /** True until every session has answered, so counts are not shown early. */
  loading: boolean;
  /** Sessions whose media could not be read. Their counts are unknown, not zero. */
  failed: string[];
}

const EMPTY: CaptureSummary = {
  bySession: {},
  countsBySession: {},
  totals: { photos: 0, videos: 0, media: 0 },
  loading: false,
  failed: [],
};

export function useCaptureSummary(sessions: CaptureSession[] | null): CaptureSummary {
  const [summary, setSummary] = useState<CaptureSummary>(EMPTY);
  const generation = useRef(0);
  // Identity of the session set, so re-rendering the list does not refetch.
  const key = (sessions ?? []).map((session) => session.id).join(",");

  useEffect(() => {
    if (sessions === null) {
      setSummary(EMPTY);
      return;
    }
    if (sessions.length === 0) {
      setSummary({ ...EMPTY, loading: false });
      return;
    }

    const controller = new AbortController();
    const current = ++generation.current;
    setSummary((previous) => ({ ...previous, loading: true }));

    Promise.all(
      sessions.map((session) =>
        // Two requests per session, and the second is the one the numbers come
        // from. The page is for the thumbnails; the summary is for the counts.
        Promise.all([
          listSessionMedia(session.id, {}, { signal: controller.signal }),
          getSessionMediaSummary(session.id, { signal: controller.signal }),
        ])
          .then(([assets, counts]) => ({ id: session.id, assets, counts, ok: true as const }))
          .catch(() => ({
            id: session.id,
            assets: [] as MediaAsset[],
            counts: null,
            ok: false as const,
          })),
      ),
    ).then((results) => {
      if (current !== generation.current || controller.signal.aborted) return;
      const bySession: Record<string, MediaAsset[]> = {};
      const countsBySession: Record<string, MediaCountSummary> = {};
      const failed: string[] = [];
      let photos = 0;
      let videos = 0;
      for (const result of results) {
        if (!result.ok || result.counts === null) {
          failed.push(result.id);
          continue;
        }
        bySession[result.id] = result.assets;
        countsBySession[result.id] = result.counts;
        // Summed from the server's answer, never from the page. A total built
        // by adding up pages is a total capped at the page size.
        photos += result.counts.photos;
        videos += result.counts.videos;
      }
      setSummary({
        bySession,
        countsBySession,
        totals: { photos, videos, media: photos + videos },
        loading: false,
        failed,
      });
    });

    return () => controller.abort();
    // `key` stands in for the session list; `sessions` itself is a new array
    // on every render of the parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return summary;
}
